<template>
  <v-container fluid class="pa-0">
    <EntityIndex
      :loading="loading"
      :total-count="totalCount"
      :table-headers="tableHeaders"
      :table-rows="tableRows"
      :table-options.sync="tableOptions"
      :selected-rows.sync="selectedRows"
      :selected-rows-actions="selectedRowsActions"
      :no-data-text="$t('defectReport.noDefectReports')"
      item-key="defectReport.id">

      <template v-slot:actions>
        <v-btn
          v-if="$featurePermissions.defectReportCreate"
          color="primary"
          elevation="2"
          class="mb-2"
          @click="$refs.defectReportDialog.openCreateNewDefectReport()"
        >
          <v-icon>mdi-plus</v-icon>
          {{ $t('actions.add') }}
        </v-btn>
      </template>

      <template v-slot:filter>
        <entity-view-filter>
          <template v-slot:filter1>
            <date-range-picker
              v-model="dateRange"
              :start-date-clearable="true"
              :end-date-clearable="true"
              @change="fetch"
            />
          </template>
          <template v-slot:filter2>
            <device-select-field
              v-model="filterDevice"
              :label="$t('device.singular')"
              @change="fetch"
              :show-select-dialog-button="true"
            />
          </template>
          <template v-slot:filter3>
            <user-select-field
              v-model="filterReporterUser"
              :label="$t('defectReport.reportingUser')"
              @change="fetch"
              :show-select-dialog-button="true"
            />
          </template>
          <template v-slot:filter4>
            <search-field
              v-model="filterText"
              hide-details="auto"
              :label="$t('plannedService.filterByText')"
              @change="fetch"
            />
          </template>

          <template v-slot:filter5>
            <fieldset class="pt-3">
              <div class="d-flex flex-column">
                <v-checkbox
                  v-model="filterStatusChoices.statusNEW"
                  :label="$t('defectReport.statusOptions.NEW')"
                  @change="fetch"
                  dense
                  hide-details
                  class="filter-status-choice"
                />
                <v-checkbox
                  v-model="filterStatusChoices.statusOPEN"
                  :label="$t('defectReport.statusOptions.OPEN')"
                  @change="fetch"
                  dense
                  hide-details
                  class="filter-status-choice"
                />
                <v-checkbox
                  v-model="filterStatusChoices.statusFIXED"
                  :label="$t('defectReport.statusOptions.FIXED')"
                  @change="fetch"
                  dense
                  hide-details
                  class="filter-status-choice"
                />
                <v-checkbox
                  v-model="filterStatusChoices.statusINVALID"
                  :label="$t('defectReport.statusOptions.INVALID')"
                  @change="fetch"
                  dense
                  hide-details
                  class="filter-status-choice"
                />
              </div>
            </fieldset>
          </template>
        </entity-view-filter>
      </template>

      <template v-slot:item.title="{ item }">
        <router-link :to="item.routerLinkTo">{{
          item.defectReport.title
        }}</router-link>
      </template>

      <template v-slot:entityListItem="props">
        <defect-report-list-item
          :item="props.item"
          :show-actions="true"
          :row-click-enabled="true"
        />
      </template>
    </EntityIndex>

    <entity-confirm-delete-dialog
      ref="confirmDeleteDialog"
      :text="$t('defectReport.deleteConfirm')"
      :text-plural="$t('defectReport.deleteConfirmPlural')"
      @confirmedDelete="deleteDefectReports"
    >
      <template v-slot:entityListItem="props">
        <defect-report-list-item :item="props.item" />
      </template>
    </entity-confirm-delete-dialog>

    <defect-report-dialog ref="defectReportDialog" @done="fetch" />
    <service-dialog ref="serviceDialog" @done="fetch" />
  </v-container>
</template>

<style scoped>
.filter-status-choice {
  margin-top: 0;
  padding-top: 0;
}
</style>

<script>
import EntityViewFilter from '@/components/entity-crud/EntityViewFilter';
import DateRangePicker from '@/components/inputs/DateRangePicker';
import SearchField from '@/components/inputs/SearchField';
import i18n from '@/i18n';
import EntityIndex from '@/components/entity-crud/EntityIndex';
import EntityConfirmDeleteDialog from '@/components/entity-crud/EntityDeleteConfirmDialog';
import DeviceSelectField from '@/components/inputs/DeviceSelectField';
import DefectReportsService from '@/services/DefectReportsService';
import DefectReportListItem from '@/views/defectreport/DefectReportListItem';
import UserSelectField from '@/components/inputs/UserSelectField';
import DefectReportDialog from '@/views/defectreport/DefectReportDialog';
import ServiceDialog from '@/views/service/ServiceDialog';

export default {
  components: {
    ServiceDialog,
    DefectReportDialog,
    UserSelectField,
    DefectReportListItem,
    DeviceSelectField,
    EntityConfirmDeleteDialog,
    EntityIndex,
    SearchField,
    EntityViewFilter,
    DateRangePicker,
  },
  name: 'DefectReportIndex',
  data() {
    return {
      defectReports: [],
      selectedRows: [],
      loading: false,
      totalCount: 0,
    };
  },
  computed: {
    dateRange: {
      get() {
        return this.$store.state.defectReportIndexState.dateRange;
      },
      set(value) {
        this.$store.commit('defectReportIndexState/setDateRange', value);
      },
    },
    filterText: {
      get() {
        return this.$store.state.defectReportIndexState.filterText;
      },
      set(value) {
        this.$store.commit('defectReportIndexState/setFilterText', value);
      },
    },
    filterDevice: {
      get() {
        return this.$store.state.defectReportIndexState.filterDevice;
      },
      set(value) {
        this.$store.commit('defectReportIndexState/setFilterDevice', value);
      },
    },
    tableOptions: {
      get() {
        return this.$store.state.defectReportIndexState.tableOptions;
      },
      set(value) {
        this.$store.commit('defectReportIndexState/setTableOptions', value);
      },
    },
    filterReporterUser: {
      get() {
        return this.$store.state.defectReportIndexState.filterReporterUser;
      },
      set(value) {
        this.$store.commit(
          'defectReportIndexState/setFilterReporterUser',
          value,
        );
      },
    },
    filterStatusChoices: {
      get() {
        return this.$store.state.defectReportIndexState.filterStatusChoices;
      },
      set(value) {
        this.$store.commit(
          'defectReportIndexState/setFilterStatusChoices',
          value,
        );
      },
    },

    tableHeaders() {
      return [
        {
          value: 'locationTop',
          text: i18n.t('location.locationTop'),
          getValue: (defectReport) => {
            return defectReport.locationTop;
          },
        },
        {
          value: 'locationSub',
          text: i18n.t('location.locationSub'),
          getValue: (defectReport) => {
            return defectReport.locationSub;
          },
        },
        {
          value: 'title',
          text: i18n.t('defectReport.title'),
          getValue: (defectReport) => {
            return defectReport.title;
          },
        },
        {
          value: 'device',
          text: i18n.t('device.singular'),
          getValue: (defectReport) => {
            if (defectReport.writeInDeviceName) {
              return defectReport.writeInDeviceName;
            }
            return this.$options.filters.device(defectReport.device);
          },
        },
        {
          value: 'created',
          text: i18n.t('defectReport.created'),
          getValue: (defectReport) => {
            return this.$options.filters.dateTime(defectReport.created);
          },
        },
        {
          value: 'priority',
          text: i18n.t('defectReport.priority'),
          getValue: (defectReport) => {
            return this.$options.filters.priority(defectReport.priority);
          },
        },
        {
          value: 'reportingUser',
          text: i18n.t('defectReport.reportingUser'),
          getValue: (defectReport) => {
            if (defectReport.signer) {
              return defectReport.signer;
            }
            return this.$options.filters.user(defectReport.reportingUser);
          },
        },
        {
          value: 'status',
          text: i18n.t('defectReport.status'),
          getValue: (defectReport) => {
            return this.$options.filters.defectReportStatus(
              defectReport.status,
            );
          },
        },
        {
          value: 'serviceDoneDate',
          text: i18n.t('defectReport.serviceDoneDate'),
          getValue: (defectReport) => {
            if (!defectReport.serviceId) {
              return null;
            }
            return this.$options.filters.date(defectReport.serviceDoneDate);
          },
        },
        {
          value: 'rowActions',
          text: '',
          getValue: (defectReport) => {
            return this.createRowActions(defectReport);
          },
        },
      ];
    },
    tableRows() {
      return this.defectReports.map((defectReport) => {
        let row = {};
        row.defectReport = defectReport;
        row.routerLinkTo = `defectreports/${defectReport.id}`;

        row.rowClick = () => {
          this.$router.push(`defectreports/${defectReport.id}`);
        };

        this.tableHeaders.forEach((header) => {
          if (header.getValue) {
            row[header.value] = header.getValue(defectReport);
          }
        });
        return row;
      });
    },

    breakpoint() {
      return this.$vuetify.breakpoint.name;
    },
    selectedRowsActions() {
      const actions = [
        {
          label: i18n.t('actions.delete'),
          icon: 'mdi-delete',
          disabled: this.selectedRows.length === 0,
          hasPermission: this.$featurePermissions.defectReportDelete,
          click: () => {
            this.confirmDeleteDefectReports(this.selectedRows);
          },
        },
      ];
      return actions.filter((action) => action.hasPermission);
    },
  },
  methods: {
    createRowActions(defectReport) {
      let actions = [
        {
          label: i18n.t('actions.show'),
          icon: 'mdi-view-list',
          hasPermission: true,
          click: () => {
            this.$router.push(`defectreports/${defectReport.id}`);
          },
        },
        {
          label: i18n.t('actions.modify'),
          icon: 'mdi-pencil-outline',
          hasPermission: this.$featurePermissions.defectReportEdit,
          click: () => {
            this.openEditDialog(defectReport.id);
          },
        },
        {
          label: i18n.t('actions.delete'),
          icon: 'mdi-delete',
          dividerBefore: true,
          hasPermission: this.$featurePermissions.defectReportDelete,
          click: () => {
            this.confirmDefectReportDelete([{ defectReport: defectReport }]);
          },
        },
      ];

      if (defectReport.serviceId == null) {
        const serviceAction = {
          label: i18n.t('defectReport.performDefectReportService'),
          icon: 'mdi-tools',
          hasPermission: this.$featurePermissions.defectFixService,
          dividerAfter: true,
          click: () => {
            this.$refs.serviceDialog.openCreateServiceForDefectReport(
              defectReport.id,
            );
          },
        };
        actions.unshift(serviceAction);
      } else {
        const serviceAction = {
          label: i18n.t('defectReport.showDefectReportService'),
          icon: 'mdi-tools',
          hasPermission: this.$featurePermissions.defectFixService,
          dividerAfter: true,
          click: () => {
            this.$router.push(`services/${defectReport.serviceId}`);
          },
        };
        actions.unshift(serviceAction);
      }

      return actions.filter((action) => action.hasPermission);
    },

    confirmDeleteDefectReports(items) {
      this.$refs.confirmDeleteDialog.show(items);
    },
    deleteDefectReports(items) {
      const defectReportIds = items.map((item) => item.defectReport.id);
      DefectReportsService.delete(defectReportIds).finally(() => {
        this.$refs.confirmDeleteDialog.close();
        this.fetch();
      });
    },

    openEditDialog(defectReportId) {
      this.$refs.defectReportDialog.openEditForDefectReport(defectReportId);
    },

    fetch() {
      this.loading = true;
      this.selectedRows = [];
      DefectReportsService.list({
        deviceId: this.filterDevice?.id,
        dateRangeStart: this.dateRange.startDate,
        dateRangeEnd: this.dateRange.endDate,
        filterText: this.filterText,
        status: this.getSelectedStatuses(),
        reportedByUserWithId: this.filterReporterUser?.id,
        limit: this.tableOptions.itemsPerPage,
        offset: (this.tableOptions.page - 1) * this.tableOptions.itemsPerPage,
        orderBy: this.tableOptions.sortBy[0],
        order: this.tableOptions.sortDesc[0] ? 'DESC' : 'ASC',
      })
        .then((result) => {
          this.loading = false;
          this.defectReports = result.data.items;
          this.totalCount = result.data.totalCount;
        })
        .catch((result) => {
          console.log(JSON.stringify(result));
          this.loading = false;
        });
    },

    getSelectedStatuses() {
      let statuses = [];
      if (this.filterStatusChoices.statusNEW) {
        statuses.push('NEW');
      }
      if (this.filterStatusChoices.statusOPEN) {
        statuses.push('OPEN');
      }
      if (this.filterStatusChoices.statusFIXED) {
        statuses.push('FIXED');
      }
      if (this.filterStatusChoices.statusINVALID) {
        statuses.push('INVALID');
      }
      return statuses;
    },
  },

  watch: {
    tableOptions: {
      handler() {
        this.fetch();
      },
      deep: true,
    },
  },
  created() {
    this.fetch();
  },
};
</script>
